import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import { Plus, RefreshCw, Repeat2, Trash2, X, Check, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react'

type CType = 'HDD'|'SSD'|'RAM'|'BATTERY'|'CPU'|'GPU'|'MOTHERBOARD'|'PSU'|'OTHER'
type CStatus = 'ACTIVE'|'RETIRED'|'REPLACED'

interface Component { id:string; type:CType; brand?:string; model?:string; serialNumber?:string; specs?:string; status:CStatus; notes?:string; installedAt:string }
interface Change { id:string; componentType:CType; reason?:string; changedBy?:string; createdAt:string; oldComponent?:Component; newComponent?:Component }

const TYPE_LABELS: Record<CType,string> = { HDD:'HDD', SSD:'SSD', RAM:'RAM', BATTERY:'Battery', CPU:'CPU', GPU:'GPU', MOTHERBOARD:'Motherboard', PSU:'PSU', OTHER:'Other' }
const STATUS_BADGE: Record<CStatus,string> = { ACTIVE:'bg-green-100 text-green-700', RETIRED:'bg-gray-100 text-gray-500', REPLACED:'bg-orange-100 text-orange-700' }
const STATUS_TH: Record<CStatus,string> = { ACTIVE:'ใช้งาน', RETIRED:'เลิกใช้', REPLACED:'เปลี่ยนแล้ว' }
const TYPES: CType[] = ['HDD','SSD','RAM','BATTERY','CPU','GPU','MOTHERBOARD','PSU','OTHER']

const ic = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white'

function AddComponentModal({ assetId, onClose }: { assetId:string; onClose:()=>void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ type:'RAM' as CType, brand:'', model:'', serialNumber:'', specs:'', notes:'' })
  const mut = useMutation({
    mutationFn: (d:any) => api.post('/components', { ...d, assetId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['components', assetId] }); onClose() },
    onError: (e:any) => alert(e.response?.data?.message||'เกิดข้อผิดพลาด'),
  })
  const set = (k:string) => (e:React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>) => setForm(f=>({...f,[k]:e.target.value}))
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">เพิ่ม Component</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div><label className="text-xs font-medium text-gray-600 block mb-1">ประเภท *</label>
            <select value={form.type} onChange={set('type')} className={ic}>
              {TYPES.map(t=><option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Brand</label><input value={form.brand} onChange={set('brand')} className={ic} placeholder="Samsung..."/></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Model</label><input value={form.model} onChange={set('model')} className={ic} placeholder="EVO 870..."/></div>
          </div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">Serial Number</label><input value={form.serialNumber} onChange={set('serialNumber')} className={ic}/></div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">Specs</label><input value={form.specs} onChange={set('specs')} className={ic} placeholder="512GB, 2.5&quot; SATA..."/></div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">หมายเหตุ</label><textarea value={form.notes} onChange={set('notes')} rows={2} className={ic}/></div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">ยกเลิก</button>
            <button onClick={()=>mut.mutate(form)} disabled={mut.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {mut.isPending?<RefreshCw size={14} className="animate-spin"/>:<Check size={14}/>} บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ReplaceModal({ component, onClose }: { component:Component; onClose:()=>void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ brand:'', model:'', serialNumber:'', specs:'', notes:'', reason:'' })
  const mut = useMutation({
    mutationFn: (d:any) => api.post(`/components/${component.id}/replace`, d),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ['components', res.data?.change?.assetId] }); onClose() },
    onError: (e:any) => alert(e.response?.data?.message||'เกิดข้อผิดพลาด'),
  })
  const set = (k:string) => (e:React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>) => setForm(f=>({...f,[k]:e.target.value}))
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Repeat2 size={16} className="text-orange-500"/> เปลี่ยน {TYPE_LABELS[component.type]}</h2>
            <p className="text-xs text-gray-400 mt-0.5">ของเดิม: {[component.brand, component.model].filter(Boolean).join(' ') || '—'} จะถูก retire</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-orange-50 rounded-xl p-3 flex items-start gap-2 text-xs text-orange-700">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
            <span>กรอกข้อมูล <strong>{TYPE_LABELS[component.type]}</strong> ตัวใหม่ที่จะติดตั้งแทน</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Brand</label><input value={form.brand} onChange={set('brand')} className={ic}/></div>
            <div><label className="text-xs font-medium text-gray-600 block mb-1">Model</label><input value={form.model} onChange={set('model')} className={ic}/></div>
          </div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">Serial Number</label><input value={form.serialNumber} onChange={set('serialNumber')} className={ic}/></div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">Specs</label><input value={form.specs} onChange={set('specs')} className={ic}/></div>
          <div><label className="text-xs font-medium text-gray-600 block mb-1">เหตุผลที่เปลี่ยน *</label><textarea value={form.reason} onChange={set('reason')} rows={2} className={ic} placeholder="เช่น เสีย, อัปเกรด, เพิ่มความจุ..."/></div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50">ยกเลิก</button>
            <button onClick={()=>mut.mutate(form)} disabled={mut.isPending||!form.reason}
              className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2">
              {mut.isPending?<RefreshCw size={14} className="animate-spin"/>:<Repeat2 size={14}/>} ยืนยันการเปลี่ยน
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ComponentsTab({ assetId, isAdmin }: { assetId:string; isAdmin:boolean }) {
  const qc = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [replaceComp, setReplaceComp] = useState<Component|null>(null)
  const [showHistory, setShowHistory] = useState(false)

  const { data: components = [], isLoading } = useQuery<Component[]>({
    queryKey: ['components', assetId],
    queryFn: () => api.get(`/components?assetId=${assetId}`).then(r=>r.data),
  })
  const { data: changes = [] } = useQuery<Change[]>({
    queryKey: ['component-changes', assetId],
    queryFn: () => api.get(`/components/changes/${assetId}`).then(r=>r.data),
    enabled: showHistory,
  })

  const deleteMut = useMutation({
    mutationFn: (id:string) => api.delete(`/components/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['components', assetId] }),
  })

  const active   = components.filter(c=>c.status==='ACTIVE')
  const retired  = components.filter(c=>c.status!=='ACTIVE')

  if (isLoading) return <div className="flex items-center justify-center py-10 text-gray-400"><RefreshCw size={18} className="animate-spin mr-2"/> กำลังโหลด...</div>

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Component ปัจจุบัน ({active.length})</h3>
        {isAdmin && <button onClick={()=>setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700"><Plus size={14}/> เพิ่ม</button>}
      </div>

      {active.length === 0 && <div className="text-center text-gray-400 py-8 text-sm border-2 border-dashed border-gray-100 rounded-xl">ยังไม่มี Component</div>}

      <div className="space-y-2">
        {active.map(c => (
          <div key={c.id} className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-blue-600">{TYPE_LABELS[c.type]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{[c.brand, c.model].filter(Boolean).join(' ') || TYPE_LABELS[c.type]}</p>
              <p className="text-xs text-gray-400">{[c.specs, c.serialNumber ? `S/N: ${c.serialNumber}` : ''].filter(Boolean).join(' · ')}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[c.status]}`}>{STATUS_TH[c.status]}</span>
            {isAdmin && (
              <div className="flex gap-1">
                <button onClick={()=>setReplaceComp(c)} className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors" title="เปลี่ยน"><Repeat2 size={14}/></button>
                <button onClick={()=>{if(confirm('ลบ component นี้?')) deleteMut.mutate(c.id)}} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="ลบ"><Trash2 size={14}/></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Retired */}
      {retired.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-400 mb-2">เลิกใช้ / เปลี่ยนแล้ว ({retired.length})</p>
          <div className="space-y-1.5">
            {retired.map(c => (
              <div key={c.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl opacity-60">
                <span className="text-xs font-bold text-gray-500 w-16 flex-shrink-0">{TYPE_LABELS[c.type]}</span>
                <span className="text-xs text-gray-600 flex-1">{[c.brand, c.model, c.specs].filter(Boolean).join(' · ')}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[c.status]}`}>{STATUS_TH[c.status]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Change History */}
      <div className="border-t border-gray-100 pt-4">
        <button onClick={()=>setShowHistory(h=>!h)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          {showHistory?<ChevronUp size={14}/>:<ChevronDown size={14}/>} ประวัติการเปลี่ยน Component
        </button>
        {showHistory && (
          <div className="mt-3 space-y-2">
            {changes.length === 0 && <p className="text-xs text-gray-400 text-center py-4">ยังไม่มีประวัติ</p>}
            {changes.map(ch => (
              <div key={ch.id} className="p-3 bg-orange-50 rounded-xl text-xs space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-orange-800">{TYPE_LABELS[ch.componentType]}</span>
                  <span className="text-gray-400">{new Date(ch.createdAt).toLocaleDateString('th-TH-u-ca-gregory')}</span>
                </div>
                {ch.reason && <p className="text-orange-700">เหตุผล: {ch.reason}</p>}
                <div className="flex items-center gap-2 text-gray-500">
                  <span className="line-through">{ch.oldComponent ? [ch.oldComponent.brand, ch.oldComponent.model].filter(Boolean).join(' ') || '—' : '—'}</span>
                  <span>→</span>
                  <span className="font-medium text-gray-700">{ch.newComponent ? [ch.newComponent.brand, ch.newComponent.model].filter(Boolean).join(' ') || '—' : '—'}</span>
                </div>
                {ch.changedBy && <p className="text-gray-400">โดย: {ch.changedBy}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd    && <AddComponentModal assetId={assetId} onClose={()=>setShowAdd(false)}/>}
      {replaceComp && <ReplaceModal component={replaceComp} onClose={()=>setReplaceComp(null)}/>}
    </div>
  )
}
